import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RefundsSummaryQuery } from "./client";
import { refundsApi } from "./client";

export const refundKeys = {
  all: ["refunds"] as const,
  summary: (query: RefundsSummaryQuery) =>
    ["refunds", "summary", query] as const,
  list: (query: OffsetPageQueryDTO) =>
    ["refunds", "requests", "list", query] as const,
  detail: (id: string) => ["refunds", "requests", "detail", id] as const,
};

export function useCreateRefundRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      targetType: "package" | "private_lesson_package";
      studentPackageId?: string;
      privateLessonPackageId?: string;
      reason: string;
      refundAmount: number;
    }) => refundsApi.requests.create(data),
    onSuccess: () => {
      toast.success("ສົ່ງຄຳຮ້ອງຂໍຄືນເງິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: refundKeys.all });
    },
  });
}

export function useRefundsSummaryQuery(query: RefundsSummaryQuery) {
  return useQuery({
    queryKey: refundKeys.summary(query),
    queryFn: () => refundsApi.getSummary(query),
  });
}

export function useRefundRequestsQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: refundKeys.list(query),
    queryFn: () => refundsApi.requests.list(query),
  });
}

export function useRefundRequestQuery(id: string | null) {
  return useQuery({
    queryKey: refundKeys.detail(id ?? ""),
    queryFn: () => refundsApi.requests.getById(id!),
    enabled: !!id,
  });
}

export function useApproveRefundRequestMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refundsApi.requests.approve(id),
    onSuccess: () => {
      toast.success("ອະນຸມັດຄຳຂໍຄືນເງິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: refundKeys.all });
    },
  });
}

export function useRejectRefundRequestMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { rejectionReason?: string }) =>
      refundsApi.requests.reject(id, data),
    onSuccess: () => {
      toast.success("ປະຕິເສດຄຳຂໍຄືນເງິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: refundKeys.all });
    },
  });
}

export function useExecuteRefundMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refundsApi.requests.execute(id),
    onSuccess: () => {
      toast.success("ບັນທຶກການຄືນເງິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: refundKeys.all });
    },
  });
}
